import React from 'react';
import { ContactFull } from 'flow/types/Contact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Sender } from 'flow/types/TicketEventData';
import { TypedMap } from 'flow/types/TypedMap';
import { twMerge } from 'tailwind-merge';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import { AvatarSize } from 'flow/components/ui-components/ChatAvatar';
import Notification from 'flow/components/ui-components/Notification';
import SenderAvatar from './internal-note/SenderAvatar';
import RobotIcon from 'flow/components/tickets/RobotIcon';

interface Props {
    contact?: TypedMap<ContactFull>;
    extensionKey?: ExtensionKey;
    showBadge?: boolean;
    badgeContent?: number | string;
    backgroundColor?: 'bg-gray-200' | 'bg-gray-300';
    senderType?: Sender;
    ignoreAvatarResource?: boolean;
    size?: AvatarSize;
    lazyLoad?: boolean;
}

const containerSizes: Record<AvatarSize, string> = {
    tiny: 'size-4',
    extraSmall: 'size-5',
    small: 'size-6',
    medium: 'size-9',
    toolbar: 'size-10',
    large: 'size-14',
    extraLarge: 'size-20',
    videoLarge: 'size-40',
};

const TicketAvatar = ({
    contact,
    extensionKey,
    showBadge,
    badgeContent,
    backgroundColor = 'bg-gray-200',
    senderType,
    ignoreAvatarResource,
    size = 'toolbar',
    lazyLoad,
}: Props) => {
    const renderAvatar = () => {
        if (senderType === 'SYSTEM') {
            return (
                <div className="flex size-full items-center justify-center rounded-full bg-gray-200 object-cover group-hover:bg-gray-300">
                    <div className="h-3/4 w-3/4 fill-gray-600">
                        <RobotIcon />
                    </div>
                </div>
            );
        }
        if (extensionKey) {
            return (
                <SenderAvatar
                    sender={extensionKey}
                    ignoreAvatarResource={ignoreAvatarResource}
                    size={size}
                    lazyLoad={lazyLoad}
                />
            );
        }
        return (
            <ContactAvatar
                contact={contact}
                shouldUseDefaultIfNoFirstNameAndNoLastName={true}
                fallBackBackgroundColorClass={twMerge(
                    'group-hover:bg-gray-300',
                    backgroundColor,
                )}
                ignoreAvatarResource={ignoreAvatarResource}
                size={size}
                lazyLoad={lazyLoad}
            />
        );
    };

    return (
        <div className={twMerge('relative', containerSizes[size])}>
            {showBadge ? (
                <Notification className="-me-1">{badgeContent}</Notification>
            ) : null}
            {renderAvatar()}
        </div>
    );
};

export default React.memo(TicketAvatar);
